<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import type { PRData } from '@/backend/metrics.backend';
import type { LocGroupByOption } from './LocChartGroupingControls.vue';

interface Props {
  prData: PRData[];
  loading?: boolean;
  error?: string | null;
  rowDimension: LocGroupByOption;
  colDimension: LocGroupByOption;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  error: null,
  rowDimension: 'github-username',
  colDimension: 'repo'
});

const emit = defineEmits<{
  retry: [];
}>();

// Get value from PR data based on dimension
function getDimensionValue(pr: PRData, dimension: LocGroupByOption): string {
  switch (dimension) {
    case 'repo':
      return pr.repo;
    case 'github-username':
      return pr.username;
    case 'team':
      return pr.teamName || 'No Team';
    case 'ai-contribution':
      return pr.aiContribution || 'Unknown';
    default:
      return 'Unknown';
  }
}

// Get unique values for a dimension
function getUniqueValues(dimension: LocGroupByOption): string[] {
  const values = new Set<string>();
  props.prData.forEach(pr => {
    values.add(getDimensionValue(pr, dimension));
  });
  return Array.from(values).sort();
}

// Calculate LOC for a specific row/col combination
function calculateLoc(rowValue: string, colValue: string): number {
  return props.prData
    .filter(pr => 
      getDimensionValue(pr, props.rowDimension) === rowValue &&
      getDimensionValue(pr, props.colDimension) === colValue
    )
    .reduce((sum, pr) => sum + pr.totalLoc, 0);
}

// Build table data
const tableData = computed(() => {
  const rowValues = getUniqueValues(props.rowDimension);
  const colValues = getUniqueValues(props.colDimension);
  
  return rowValues.map(rowValue => {
    const row: Record<string, any> = {
      _rowLabel: rowValue
    };
    
    colValues.forEach(colValue => {
      row[colValue] = calculateLoc(rowValue, colValue);
    });
    
    return row;
  });
});

// Get column values for dynamic columns
const columnValues = computed(() => getUniqueValues(props.colDimension));

// Get maximum value for color scaling
const maxValue = computed(() => {
  let max = 0;
  tableData.value.forEach(row => {
    columnValues.value.forEach(colValue => {
      const value = row[colValue] || 0;
      if (value > max) max = value;
    });
  });
  return max;
});

// Get cell background color based on value
function getCellBackground(value: number): string {
  if (!value || maxValue.value === 0) return 'transparent';
  
  // Calculate intensity (0 to 1)
  const intensity = value / maxValue.value;
  
  // Use a green color scale - lighter green to darker green
  // RGB: (220, 252, 231) to (22, 163, 74) - Tailwind green-100 to green-600
  const r = Math.round(220 - (220 - 22) * intensity);
  const g = Math.round(252 - (252 - 163) * intensity);
  const b = Math.round(231 - (231 - 74) * intensity);
  
  return `rgb(${r}, ${g}, ${b})`;
}

// Get text color for contrast
function getTextColor(value: number): string {
  if (!value || maxValue.value === 0) return '';
  
  const intensity = value / maxValue.value;
  // Switch to white text when intensity is high enough for better contrast
  return intensity > 0.6 ? 'white' : '';
}

// Format LOC number with commas
function formatLoc(value: number): string {
  if (!value) return '0';
  return value.toLocaleString();
}

// Get dimension label
function getDimensionLabel(dimension: LocGroupByOption): string {
  const labels: Partial<Record<LocGroupByOption, string>> = {
    'repo': 'Repository',
    'github-username': 'GitHub User',
    'team': 'Team',
    'ai-contribution': 'AI Contribution'
  };
  return labels[dimension] || dimension;
}
</script>

<template>
  <div class="metrics-loc-table">
    <!-- Error State -->
    <div v-if="error" class="flex flex-col items-center justify-center p-8 text-center">
      <i class="pi pi-exclamation-triangle text-4xl text-red-500 mb-4"></i>
      <p class="text-lg font-semibold mb-2">Failed to load data</p>
      <p class="text-sm text-muted-color mb-4">{{ error }}</p>
      <button
        @click="emit('retry')"
        class="px-4 py-2 bg-primary text-primary-contrast rounded hover:bg-primary-emphasis"
      >
        <i class="pi pi-refresh mr-2"></i>
        Retry
      </button>
    </div>

    <!-- Table -->
    <div v-else-if="!loading && prData.length > 0" class="overflow-x-auto">
      <DataTable
        :value="tableData"
        :loading="loading"
        showGridlines
        size="small"
        sortMode="multiple"
        class="loc-table"
        :pt="{
          root: { class: 'text-sm' }
        }"
      >
        <!-- Row header column -->
        <Column
          field="_rowLabel"
          :header="getDimensionLabel(rowDimension)"
          sortable
          frozen
          :pt="{
            headerCell: { class: 'font-semibold bg-surface-100 dark:bg-surface-800' },
            bodyCell: { class: 'font-medium text-xs' }
          }"
        />
        
        <!-- Dynamic columns for each column dimension value -->
        <Column
          v-for="colValue in columnValues"
          :key="colValue"
          :field="colValue"
          :header="colValue"
          sortable
          :pt="{
            headerCell: { class: 'text-center bg-surface-50 dark:bg-surface-900' },
            bodyCell: { class: 'text-right' }
          }"
        >
          <template #body="slotProps">
            <div 
              class="px-3 py-2 -m-2 -mt-[0.45rem]"
              :style="{ 
                backgroundColor: getCellBackground(slotProps.data[colValue]),
                color: getTextColor(slotProps.data[colValue])
              }"
            >
              <span :class="{ 'text-muted-color': !slotProps.data[colValue] && !getTextColor(slotProps.data[colValue]) }">
                {{ formatLoc(slotProps.data[colValue]) }}
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Empty State -->
    <div v-else-if="!loading && prData.length === 0" class="flex flex-col items-center justify-center p-8 text-center">
      <i class="pi pi-inbox text-4xl text-muted-color mb-4"></i>
      <p class="text-lg font-semibold mb-2">No Data Available</p>
      <p class="text-sm text-muted-color">No pull requests found for the selected period</p>
    </div>

    <!-- Loading State -->
    <div v-else class="flex items-center justify-center p-8">
      <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
    </div>
  </div>
</template>

<style scoped>
.metrics-loc-table {
  width: 100%;
}

.loc-table :deep(.p-datatable-footer) {
  background-color: var(--p-primary-50);
  border-top: 2px solid var(--p-primary-200);
}

:root.dark .loc-table :deep(.p-datatable-footer) {
  background-color: rgba(var(--p-primary-900-rgb), 0.2);
  border-top-color: var(--p-primary-800);
}

.loc-table :deep(.p-datatable-frozen-column) {
  background-color: var(--p-surface-0);
  position: sticky !important;
}

:root.dark .loc-table :deep(.p-datatable-frozen-column) {
  background-color: var(--p-surface-950);
}
</style>
